import type { TabDef, StatusMapping } from "./types";

export const EVENT_TABS: TabDef[] = [
  {
    id: "customers",
    title: "Customers",
    description:
      "Control which customer updates are sent to Iterable to create and keep user profiles in sync — including profile changes, tags, and consent state.",
    events: [
      {
        key: "CUSTOMER_CREATE",
        label: "Customer created",
        help: "Triggered when a new customer record is created.",
        topics: ["customers/create"],
      },
      {
        key: "CUSTOMER_UPDATE",
        label: "Customer updated",
        help: "Triggered when a customer record is updated.",
        topics: ["customers/update"],
      },
      {
        key: "CUSTOMER_TAGS",
        label: "Customer tags updated",
        help: "Sync when tags are added or removed from a customer profile.",
        topics: ["customer.tags_added", "customer.tags_removed"],
        iterableFieldName: "tags",
      },
      {
        key: "CUSTOMERS_PURCHASING_SUMMARY",
        label: "Customer purchasing summary",
        help: "Sync when customer purchasing summary fields are updated.",
        topics: ["customers/purchasing_summary"],
        iterableFieldName: "numberOfOrders, amountSpent, lastOrderId",
      },
      {
        key: "CUSTOMERS_EMAIL_MARKETING_CONSENT_UPDATE",
        label: "Email marketing consent updated",
        help: "Sync when a customer's email marketing consent state changes.",
        topics: ["customers_email_marketing_consent/update"],
        iterableFieldName: "email_marketing_consent {}",
      },
      {
        key: "CUSTOMERS_MARKETING_CONSENT_UPDATE",
        label: "SMS marketing consent updated",
        help: "Sync when a customer's SMS marketing consent state changes.",
        topics: ["customers_marketing_consent/update"],
        iterableFieldName: "sms_marketing_consent {}",
      },
      {
        key: "CUSTOMERS_DISABLE",
        label: "Customer disabled",
        help: "Triggered when a customer account is disabled.",
        topics: ["customers/disable"],
      },
      {
        key: "CUSTOMERS_ENABLE",
        label: "Customer enabled",
        help: "Triggered when a customer account is enabled.",
        topics: ["customers/enable"],
      },
    ],
  },
  {
    id: "orders",
    title: "Orders",
    description:
      "Track purchases and order updates to Iterable for attribution and post-purchase communication. Order creation and updates upsert to the same purchase event, while draft order creation and updates upsert to the same createDraftOrder custom event.",
    events: [
      {
        key: "ORDERS_CREATE",
        label: "Order created",
        help: "Fires when an order is placed. Commonly used to track purchases.",
        topics: ["orders/create"],
      },
      {
        key: "ORDERS_UPDATE",
        label: "Order updated",
        help: "Fires when order properties change (totals, notes, line items, etc.).",
        topics: ["orders/updated"],
      },
      {
        key: "DRAFT_ORDERS_CREATE",
        label: "Draft order created",
        help: "Triggered when a draft order is created.",
        topics: ["draft_orders/create"],
        iterableFieldName: "createDraftOrder",
      },
      {
        key: "DRAFT_ORDERS_UPDATED",
        label: "Draft order updated",
        help: "Triggered when a draft order is updated.",
        topics: ["draft_orders/update"],
        iterableFieldName: "createDraftOrder",
      },
      {
        key: "ORDERS_CANCELLED",
        label: "Order cancelled",
        help: "Triggered when an order is cancelled.",
        topics: ["orders/cancelled"],
        iterableFieldName: "cancelOrder",
      },
      {
        key: "REFUNDS_CREATE",
        label: "Refund created",
        help: "Triggered when a refund is issued.",
        topics: ["refunds/create"],
        iterableFieldName: "createRefund",
      },
    ],
  },
  {
    id: "checkouts",
    title: "Checkouts",
    description:
      "Sync checkout creation and update events to Iterable as custom events. Both events upsert into the same Iterable event (createCheckout).",
    events: [
      {
        key: "CHECKOUTS_CREATE",
        label: "Checkout created",
        help: "Triggered when a checkout is created.",
        topics: ["checkouts/create"],
      },
      {
        key: "CHECKOUTS_UPDATE",
        label: "Checkout updated",
        help: "Triggered when a checkout is updated (e.g. shipping/billing/contact updates during checkout).",
        topics: ["checkouts/update"],
      },
    ],
  },
  {
    id: "fulfillments",
    title: "Fulfillments",
    description:
      "Track fulfillment lifecycle as custom events to power shipment and delivery notifications. Fulfillment creation and updates upsert to the same createFulfillment custom event.",
    events: [
      {
        key: "FULFILLMENTS_CREATE",
        label: "Fulfillment created",
        help: "A fulfillment is created for an order.",
        topics: ["fulfillments/create"],
        iterableFieldName: "createFulfillment",
      },
      {
        key: "FULFILLMENTS_UPDATE",
        label: "Fulfillment updated",
        help: "Triggered when a fulfillment record is updated in Shopify (e.g. tracking number added, status changed, etc.).",
        topics: ["fulfillments/update"],
        iterableFieldName: "createFulfillment",
      },
      {
        label: "Fulfillment delivery events",
        key: "FULFILLMENT_EVENTS_CREATE",
        help: "Triggered when a carrier status update is logged for a fulfillment (e.g. in transit, out for delivery, delivered). These events appear as individual custom events.",
        topics: ["fulfillment_events/create"],
      },
    ],
  },
];

export const FULFILLMENT_STATUS_MAPPINGS: StatusMapping[] = [
  { status: "confirmed", event: "orderShippingConfirmed" },
  { status: "in_transit", event: "orderInTransit" },
  { status: "out_for_delivery", event: "orderOutForDelivery" },
  { status: "delivered", event: "orderDelivered" },
  { status: "failure", event: "orderDeliveryFailure" },
  { status: "attempted_delivery", event: "orderAttemptedDelivery" },
  { status: "label_printed", event: "orderLabelPrinted" },
  { status: "label_purchased", event: "orderLabelPurchased" },
  { status: "ready_for_pickup", event: "orderReadyForPickup" },
  { status: "carrier_picked_up", event: "orderCarrierPickedUp" },
  { status: "delayed", event: "orderDelayed" },
];
